/**
 * The Civil preview, at `/civil` (components/civil/, docs/foundations.md §8).
 *
 * Quantities arrive formatted (`q`), as in foundations.ts; units are symbols
 * and are not translated. Beam names are trade names (IPE, glulam sizes) with
 * the material in the reader's language.
 */
import type { BeamId, Slot } from '../civil';

/* ── English ──────────────────────────────────────────────────────────────── */

export const en = {
  docTitle: 'Civil preview · EngineerOS',
  kicker: 'A preview of civil engineering',
  title: 'A house, from the roof down',
  lede:
    'Five tasks on one house: the roof, a beam over a door, the footings, the walls against the wind, and a check on Iris. ' +
    'Each one is a decision a civil engineer makes, and the page tests it.',
  honest:
    'A preview, with models simplified on purpose; every step says what it leaves out. Real houses are designed to a building ' +
    'code by a qualified engineer.',
  progress: (done: number, total: number) => `${done} of ${total} done`,
  finish: {
    title: 'You finished the Civil preview',
    lede: 'Five decisions, each one tested. If you want the full Civil course, say so below: it is how we decide what to build next.',
  },

  steps: {
    rafters: {
      idea: 'Loads from above',
      title: 'Snow on the roof',
      story:
        "A house starts at the top. Roof tiles and a winter's snow press on this roof with 1.5 kN on every square metre, and " +
        'the rafters carry it down to the walls. Closer rafters share the load; deeper ones bend less. Both cost timber.',
      task:
        "Choose the rafters' spacing and depth. They must stay under the timber's 7.5 MPa bending limit and use no more than 20 " +
        'litres of timber for each metre of roof.',
      debrief:
        'Two answers work: 200 mm deep at 600 mm apart, or 150 mm deep at 400 mm. Depth is the cheap way to strength, because ' +
        'bending strength grows with the square of depth. Real roofs are worked out just like this, from the load down.',
      leavesOut: 'Wind lifting the roof, the pitch, the joints at each end, and the factors a design code adds for drifting snow and long-term loads.',
    },
    lintel: {
      idea: 'Beams and openings',
      title: 'A beam over the garage door',
      story:
        'The owner wants a 3 m garage door in a wall that carries the floor and roof above: 12 kN on every metre. Cut the ' +
        'opening and something must bridge it. A beam over an opening fails in two ways: it breaks, or it sags enough to crack ' +
        'the bricks above.',
      task: "Choose a beam that is strong enough and stiff enough: stress under its material's limit, and sag under the span divided by 360.",
      debrief:
        'Steel is so much stronger that a 160 mm steel beam does what 360 mm of glued timber does. The 270 mm glulam was stiff ' +
        'enough and would still have broken: strength and stiffness are different questions, and a beam has to answer both.',
      leavesOut: "The beam's ends bearing on the brickwork, its own weight, fire protection, and steel's habit of twisting sideways when nothing holds it.",
    },
    footings: {
      idea: 'Ground',
      title: 'Same wall, different ground',
      story:
        'Everything ends in the ground. Each wall brings 60 kN per metre down to a concrete strip footing. The soil survey says ' +
        'the front sits on firm sand that can take 150 kPa, but the back is soft clay that takes only 75.',
      task: 'Choose a width for each footing so the pressure under it stays within what its soil can carry. Concrete costs money: wider is not free.',
      debrief:
        'The soft end needs more than twice the width. Get it wrong and that end sinks more than the other, which is how houses ' +
        'crack from the ground up. That is why the soil survey comes first.',
      leavesOut: 'Settlement over the years, groundwater, frost, and clay that swells and shrinks with the seasons.',
    },
    bracing: {
      idea: 'Wind',
      title: 'Brace the wall against the wind',
      story:
        'Wind on the end of the house pushes each side wall with 19.2 kN. A wall of plain panels folds like a pack of cards; a ' +
        'panel braced with a diagonal or a plywood skin resists 5 kN. The owner also wants three windows in this wall.',
      task: 'Click the eight panels to make each one plain, braced or a window. Resist the wind, brace both corners, and keep three windows.',
      debrief:
        'Four braced panels resist 20 kN, just over the 19.2, with the corners braced where the force arrives. Every window is ' +
        'a panel that cannot brace, which is why architects and engineers negotiate over walls.',
      leavesOut: 'Wind from other directions, the roof and floor that carry the force to the walls, and the hold-downs that stop a braced panel lifting.',
    },
    'civil-ai': {
      idea: 'Checking the AI',
      title: 'Iris picks a steel beam',
      story: 'The owner now wants a wider door, 3.6 m, under the same wall. Iris has an answer ready.',
      task: "Test Iris's beam over the 3.6 m opening. If it fails, choose one that works.",
      debrief:
        'Iris reasoned from the material, not the numbers. Bending grows with the square of the span, so 20% wider is 44% more ' +
        'stress. Steel being strong does not make a small steel beam strong enough.',
      leavesOut: 'Everything the garage beam left out.',
    },
  },

  rafters: {
    spacing: 'Spacing',
    depth: 'Depth',
    stress: (q: string, allow: string) => `Stress ${q} of ${allow}`,
    timber: (q: string, budget: string) => `timber ${q} of ${budget}`,
    ok: 'It carries the snow, within the timber budget.',
    weak: (q: string) => `At ${q} the rafters break under the snow.`,
    wasteful: (q: string) => `Strong enough, but ${q} of timber is over the budget.`,
  },
  lintel: {
    beam: 'Beam',
    opening: (q: string) => `Opening ${q}`,
    stress: (q: string, allow: string) => `Stress ${q} of ${allow}`,
    sag: (q: string, limit: string) => `sag ${q} of ${limit}`,
    ok: 'Strong and stiff enough. The bricks above stay whole.',
    weak: 'The beam is overstressed and breaks.',
    bouncy: 'Strong enough, but it sags more than the bricks above can take. They crack.',
  },
  beams: {
    'timber-2x200': 'Timber 2 × 50×200',
    'glulam-270': 'Glulam 90×270',
    'glulam-360': 'Glulam 90×360',
    'ipe-140': 'Steel IPE 140',
    'ipe-160': 'Steel IPE 160',
    'ipe-200': 'Steel IPE 200',
  } as Record<BeamId, string>,
  footings: {
    front: 'Front, sand',
    back: 'Back, soft clay',
    frontWidth: 'Front width',
    backWidth: 'Back width',
    pressure: (q: string, allow: string) => `${q} of ${allow}`,
    ok: 'Both ends settle a little, and the same. The house stays level.',
    sinks: (where: string) => `${where}: the soil gives way and that end sinks. The house tilts.`,
  },
  bracing: {
    wall: 'Side wall, eight panels',
    kinds: { plain: 'Plain', brace: 'Braced', window: 'Window' } as Record<Slot, string>,
    capacity: (q: string, demand: string) => `Resists ${q} of ${demand}`,
    count: (n: number, want: number) => `${n} of ${want} windows`,
    each: (q: string) => `each braced panel ${q}`,
    hint: 'Click a panel to change it: plain, braced, window.',
    ok: 'The wall stands, with three windows.',
    racks: (cap: string, demand: string) => `The braced panels resist ${cap}; the wind brings ${demand}. The wall racks over.`,
    ends: 'Strong enough in total, but an unbraced corner lets the wall fold where the force arrives.',
    windows: (n: number) => `It stands, but the owner asked for ${n} windows.`,
  },
  iris: "Use an IPE 140. It's steel, so it is far stronger than any timber beam. It will be fine over 3.6 m.",
};

/* ── Arabic ───────────────────────────────────────────────────────────────── */

export const ar: typeof en = {
  docTitle: 'معاينة الهندسة المدنية · EngineerOS',
  kicker: 'معاينة للهندسة المدنية',
  title: 'بيت، من السقف إلى الأرض',
  lede:
    'خمس مهام على بيت واحد: السقف، وجسر فوق باب، والأساسات، والجدران في وجه الرياح، والتحقّق من Iris. كل واحدة قرار يتخذه ' +
    'مهندس مدني، والصفحة تختبره.',
  honest: 'معاينة، والنماذج فيها مبسّطة عن قصد؛ كل خطوة تقول ما تتركه جانبًا. البيوت الحقيقية يصمّمها مهندس مؤهّل وفق كود البناء.',
  progress: (done: number, total: number) => `أنجزت ${done} من ${total}`,
  finish: {
    title: 'أتممت معاينة الهندسة المدنية',
    lede: 'خمسة قرارات، اختُبر كل واحد منها. إن أردت مسار الهندسة المدنية الكامل فقل ذلك أدناه: هكذا نقرّر ما نبنيه بعد ذلك.',
  },

  steps: {
    rafters: {
      idea: 'الأحمال من الأعلى',
      title: 'ثلج على السقف',
      story:
        'البيت يبدأ من الأعلى. القرميد وثلج الشتاء يضغطان على هذا السقف بـ 1.5 كيلونيوتن على كل متر مربع، والروافد تنقل ذلك إلى الجدران. ' +
        'الروافد المتقاربة تتقاسم الحمل، والأعمق تنحني أقل. وكلاهما يكلّف خشبًا.',
      task: 'اختر المسافة بين الروافد وعمقها. يجب أن تبقى تحت حدّ الانحناء للخشب 7.5 ميغاباسكال، وألّا تستهلك أكثر من 20 لترًا من الخشب لكل متر من السقف.',
      debrief:
        'هناك جوابان صحيحان: عمق 200 مم كل 600 مم، أو عمق 150 مم كل 400 مم. العمق هو الطريق الرخيص إلى القوّة، لأن مقاومة الانحناء ' +
        'تزداد بمربّع العمق. هكذا تُحسب السقوف الحقيقية، من الحمل نزولًا.',
      leavesOut: 'الرياح التي ترفع السقف، وميله، والوصلات في الطرفين، والمعاملات التي يضيفها كود التصميم لتراكم الثلج والأحمال الطويلة.',
    },
    lintel: {
      idea: 'الجسور والفتحات',
      title: 'جسر فوق باب المرآب',
      story:
        'يريد صاحب البيت باب مرآب بعرض 3 م في جدار يحمل الأرضية والسقف فوقه: 12 كيلونيوتن على كل متر. افتح الفتحة ولا بد أن شيئًا ' +
        'سيعبرها. الجسر فوق الفتحة يفشل بطريقتين: ينكسر، أو ينحني بما يكفي لتشقّق الطوب فوقه.',
      task: 'اختر جسرًا قويًا وصلبًا بما يكفي: إجهاد تحت حدّ مادّته، وانحناء أقل من البحر مقسومًا على 360.',
      debrief:
        'الفولاذ أقوى بكثير، حتى إن جسرًا فولاذيًا عمقه 160 مم يؤدّي ما يؤدّيه 360 مم من الخشب الملصوق. خشب 270 مم كان صلبًا بما يكفي ' +
        'ومع ذلك كان سينكسر: القوّة والصلابة سؤالان مختلفان، وعلى الجسر أن يجيب عن كليهما.',
      leavesOut: 'ارتكاز طرفي الجسر على الطوب، ووزنه الذاتي، والحماية من الحريق، وميل الفولاذ إلى الالتواء جانبًا حين لا يمسكه شيء.',
    },
    footings: {
      idea: 'الأرض',
      title: 'الجدار نفسه، وأرض مختلفة',
      story:
        'كل شيء ينتهي في الأرض. كل جدار ينقل 60 كيلونيوتن لكل متر إلى أساس شريطي من الخرسانة. يقول تقرير التربة إن الواجهة الأمامية على ' +
        'رمل متماسك يحتمل 150 كيلوباسكال، أما الخلفية فعلى طين ليّن لا يحتمل إلا 75.',
      task: 'اختر عرضًا لكل أساس بحيث يبقى الضغط تحته ضمن ما تحتمله تربته. الخرسانة تكلّف مالًا: الأعرض ليس مجانيًا.',
      debrief:
        'الطرف الليّن يحتاج إلى أكثر من ضعف العرض. أخطئ فيه فيهبط ذلك الطرف أكثر من الآخر، وهكذا تتشقّق البيوت من الأرض صعودًا. ' +
        'لهذا يأتي تقرير التربة أولًا.',
      leavesOut: 'الهبوط على مرّ السنين، والمياه الجوفية، والصقيع، والطين الذي ينتفخ وينكمش مع الفصول.',
    },
    bracing: {
      idea: 'الرياح',
      title: 'قوِّ الجدار ضد الرياح',
      story:
        'الرياح على طرف البيت تدفع كل جدار جانبي بـ 19.2 كيلونيوتن. جدار من ألواح عادية ينطوي كأوراق اللعب؛ أما اللوح المقوّى بقطر ' +
        'مائل أو بكسوة من الخشب الرقائقي فيقاوم 5 كيلونيوتن. ويريد صاحب البيت أيضًا ثلاث نوافذ في هذا الجدار.',
      task: 'انقر الألواح الثمانية لتجعل كلًّا منها عاديًا أو مقوّى أو نافذة. قاوم الرياح، وقوِّ الزاويتين، واحتفظ بثلاث نوافذ.',
      debrief:
        'أربعة ألواح مقوّاة تقاوم 20 كيلونيوتن، أكثر بقليل من 19.2، والزاويتان مقوّاتان حيث تصل القوّة. كل نافذة لوح لا يستطيع ' +
        'التقوية، ولهذا يتفاوض المعماريون والمهندسون على الجدران.',
      leavesOut: 'الرياح من الاتجاهات الأخرى، والسقف والأرضية اللذان ينقلان القوّة إلى الجدران، والمثبّتات التي تمنع اللوح المقوّى من الارتفاع.',
    },
    'civil-ai': {
      idea: 'التحقّق من الذكاء الاصطناعي',
      title: 'Iris تختار جسرًا فولاذيًا',
      story: 'يريد صاحب البيت الآن بابًا أعرض، 3.6 م، تحت الجدار نفسه. و Iris لديها جواب جاهز.',
      task: 'اختبر جسر Iris فوق فتحة 3.6 م. إن فشل فاختر جسرًا يعمل.',
      debrief:
        'استدلّت Iris من المادّة لا من الأرقام. الانحناء يزداد بمربّع البحر، فعرض أكبر بـ 20% يعني إجهادًا أكبر بـ 44%. ' +
        'كون الفولاذ قويًا لا يجعل جسرًا فولاذيًا صغيرًا قويًا بما يكفي.',
      leavesOut: 'كل ما تركه جسر المرآب جانبًا.',
    },
  },

  rafters: {
    spacing: 'المسافة',
    depth: 'العمق',
    stress: (q: string, allow: string) => `الإجهاد ${q} من ${allow}`,
    timber: (q: string, budget: string) => `الخشب ${q} من ${budget}`,
    ok: 'يحمل الثلج، ضمن ميزانية الخشب.',
    weak: (q: string) => `عند ${q} تنكسر الروافد تحت الثلج.`,
    wasteful: (q: string) => `قوي بما يكفي، لكن ${q} من الخشب يتجاوز الميزانية.`,
  },
  lintel: {
    beam: 'الجسر',
    opening: (q: string) => `الفتحة ${q}`,
    stress: (q: string, allow: string) => `الإجهاد ${q} من ${allow}`,
    sag: (q: string, limit: string) => `الانحناء ${q} من ${limit}`,
    ok: 'قوي وصلب بما يكفي. الطوب فوقه يبقى سليمًا.',
    weak: 'الإجهاد على الجسر أكبر من حدّه، فينكسر.',
    bouncy: 'قوي بما يكفي، لكنه ينحني أكثر مما يحتمله الطوب فوقه. فيتشقّق.',
  },
  beams: {
    'timber-2x200': 'خشب 2 × 50×200',
    'glulam-270': 'خشب ملصوق 90×270',
    'glulam-360': 'خشب ملصوق 90×360',
    'ipe-140': 'فولاذ IPE 140',
    'ipe-160': 'فولاذ IPE 160',
    'ipe-200': 'فولاذ IPE 200',
  },
  footings: {
    front: 'الأمام، رمل',
    back: 'الخلف، طين ليّن',
    frontWidth: 'عرض الأمامي',
    backWidth: 'عرض الخلفي',
    pressure: (q: string, allow: string) => `${q} من ${allow}`,
    ok: 'يهبط الطرفان قليلًا وبالقدر نفسه. يبقى البيت مستويًا.',
    sinks: (where: string) => `${where}: تستسلم التربة فيهبط ذلك الطرف. يميل البيت.`,
  },
  bracing: {
    wall: 'الجدار الجانبي، ثمانية ألواح',
    kinds: { plain: 'عادي', brace: 'مقوّى', window: 'نافذة' },
    capacity: (q: string, demand: string) => `يقاوم ${q} من ${demand}`,
    count: (n: number, want: number) => `${n} من ${want} نوافذ`,
    each: (q: string) => `كل لوح مقوّى ${q}`,
    hint: 'انقر لوحًا لتغييره: عادي، مقوّى، نافذة.',
    ok: 'يصمد الجدار، ومعه ثلاث نوافذ.',
    racks: (cap: string, demand: string) => `الألواح المقوّاة تقاوم ${cap}، والرياح تأتي بـ ${demand}. ينطوي الجدار.`,
    ends: 'قوي بما يكفي في المجموع، لكن زاوية غير مقوّاة تدع الجدار ينطوي حيث تصل القوّة.',
    windows: (n: number) => `يصمد، لكن صاحب البيت طلب ${n} نوافذ.`,
  },
  iris: 'استعمل IPE 140. إنه فولاذ، فهو أقوى بكثير من أي جسر خشبي. سيكون جيدًا فوق 3.6 م.',
};

/* ── German ───────────────────────────────────────────────────────────────── */

export const de: typeof en = {
  docTitle: 'Vorschau Bauingenieurwesen · EngineerOS',
  kicker: 'Eine Vorschau auf das Bauingenieurwesen',
  title: 'Ein Haus, vom Dach bis zum Boden',
  lede:
    'Fünf Aufgaben an einem Haus: das Dach, ein Sturz über einem Tor, die Fundamente, die Wände gegen den Wind und ein Blick ' +
    'auf Iris. Jede ist eine Entscheidung, die Bauingenieure treffen, und die Seite testet sie.',
  honest:
    'Eine Vorschau mit absichtlich vereinfachten Modellen; jeder Schritt sagt, was er weglässt. Echte Häuser plant eine ' +
    'qualifizierte Fachperson nach den Baunormen.',
  progress: (done: number, total: number) => `${done} von ${total} erledigt`,
  finish: {
    title: 'Du hast die Vorschau Bauingenieurwesen abgeschlossen',
    lede: 'Fünf Entscheidungen, jede getestet. Wenn du den vollen Kurs willst, sag es unten: So entscheiden wir, was wir als Nächstes bauen.',
  },

  steps: {
    rafters: {
      idea: 'Lasten von oben',
      title: 'Schnee auf dem Dach',
      story:
        'Ein Haus beginnt oben. Ziegel und Winterschnee drücken mit 1,5 kN auf jeden Quadratmeter dieses Dachs, und die Sparren ' +
        'tragen das zu den Wänden. Engere Sparren teilen sich die Last, höhere biegen sich weniger. Beides kostet Holz.',
      task:
        'Wähle Abstand und Höhe der Sparren. Sie müssen unter der Biegegrenze des Holzes von 7,5 MPa bleiben und dürfen höchstens ' +
        '20 Liter Holz pro Meter Dach verbrauchen.',
      debrief:
        'Zwei Antworten funktionieren: 200 mm hoch bei 600 mm Abstand oder 150 mm hoch bei 400 mm. Höhe ist der billige Weg zur ' +
        'Festigkeit, weil die Biegefestigkeit mit dem Quadrat der Höhe wächst. Echte Dächer werden genau so gerechnet, von der Last nach unten.',
      leavesOut: 'Wind, der das Dach anhebt, die Neigung, die Anschlüsse an beiden Enden und die Faktoren, die eine Norm für Schneeverwehungen und Dauerlasten ergänzt.',
    },
    lintel: {
      idea: 'Träger und Öffnungen',
      title: 'Ein Sturz über dem Garagentor',
      story:
        'Der Bauherr will ein 3 m breites Garagentor in einer Wand, die Decke und Dach darüber trägt: 12 kN pro Meter. Wer die ' +
        'Öffnung schneidet, muss sie überbrücken. Ein Sturz versagt auf zwei Arten: Er bricht, oder er biegt sich so weit durch, ' +
        'dass das Mauerwerk darüber reißt.',
      task: 'Wähle einen Träger, der fest und steif genug ist: Spannung unter der Grenze seines Materials, Durchbiegung unter Spannweite durch 360.',
      debrief:
        'Stahl ist so viel fester, dass ein 160 mm hoher Stahlträger leistet, was 360 mm Brettschichtholz leisten. Das 270-mm-' +
        'Brettschichtholz war steif genug und wäre trotzdem gebrochen: Festigkeit und Steifigkeit sind verschiedene Fragen, und ' +
        'ein Träger muss beide beantworten.',
      leavesOut: 'Die Auflager im Mauerwerk, das Eigengewicht, den Brandschutz und die Neigung von Stahl, seitlich zu kippen, wenn ihn nichts hält.',
    },
    footings: {
      idea: 'Baugrund',
      title: 'Gleiche Wand, anderer Boden',
      story:
        'Alles endet im Boden. Jede Wand bringt 60 kN pro Meter auf ein Streifenfundament aus Beton. Laut Bodengutachten steht die ' +
        'Vorderseite auf festem Sand, der 150 kPa aufnimmt, die Rückseite aber auf weichem Ton, der nur 75 aufnimmt.',
      task: 'Wähle für jedes Fundament eine Breite, sodass der Druck darunter im Rahmen dessen bleibt, was sein Boden trägt. Beton kostet Geld: breiter ist nicht umsonst.',
      debrief:
        'Das weiche Ende braucht mehr als die doppelte Breite. Liegst du falsch, sinkt dieses Ende stärker als das andere, und so ' +
        'reißen Häuser vom Boden aus. Deshalb kommt das Bodengutachten zuerst.',
      leavesOut: 'Setzungen über Jahre, Grundwasser, Frost und Ton, der mit den Jahreszeiten quillt und schrumpft.',
    },
    bracing: {
      idea: 'Wind',
      title: 'Die Wand gegen den Wind aussteifen',
      story:
        'Wind auf die Giebelseite drückt jede Seitenwand mit 19,2 kN. Eine Wand aus einfachen Feldern klappt um wie ein Kartenhaus; ' +
        'ein Feld mit Diagonale oder Sperrholzbeplankung widersteht 5 kN. Der Bauherr will in dieser Wand außerdem drei Fenster.',
      task: 'Klicke die acht Felder an, um jedes einfach, ausgesteift oder zum Fenster zu machen. Halte dem Wind stand, steife beide Ecken aus und behalte drei Fenster.',
      debrief:
        'Vier ausgesteifte Felder widerstehen 20 kN, knapp über 19,2, mit ausgesteiften Ecken dort, wo die Kraft ankommt. Jedes ' +
        'Fenster ist ein Feld, das nicht aussteifen kann, deshalb verhandeln Architektur und Tragwerksplanung über Wände.',
      leavesOut: 'Wind aus anderen Richtungen, Dach und Decke, die die Kraft in die Wände leiten, und die Zuganker, die ein ausgesteiftes Feld am Abheben hindern.',
    },
    'civil-ai': {
      idea: 'Die KI überprüfen',
      title: 'Iris wählt einen Stahlträger',
      story: 'Jetzt soll das Tor breiter werden, 3,6 m, unter derselben Wand. Iris hat schon eine Antwort.',
      task: 'Teste den Träger von Iris über der 3,6-m-Öffnung. Wenn er versagt, wähle einen, der hält.',
      debrief:
        'Iris hat vom Material her gedacht, nicht von den Zahlen. Die Biegung wächst mit dem Quadrat der Spannweite, 20 % breiter ' +
        'sind also 44 % mehr Spannung. Dass Stahl fest ist, macht einen kleinen Stahlträger nicht fest genug.',
      leavesOut: 'Alles, was der Garagensturz wegließ.',
    },
  },

  rafters: {
    spacing: 'Abstand',
    depth: 'Höhe',
    stress: (q: string, allow: string) => `Spannung ${q} von ${allow}`,
    timber: (q: string, budget: string) => `Holz ${q} von ${budget}`,
    ok: 'Es trägt den Schnee, im Holzbudget.',
    weak: (q: string) => `Bei ${q} brechen die Sparren unter dem Schnee.`,
    wasteful: (q: string) => `Fest genug, aber ${q} Holz sprengen das Budget.`,
  },
  lintel: {
    beam: 'Träger',
    opening: (q: string) => `Öffnung ${q}`,
    stress: (q: string, allow: string) => `Spannung ${q} von ${allow}`,
    sag: (q: string, limit: string) => `Durchbiegung ${q} von ${limit}`,
    ok: 'Fest und steif genug. Das Mauerwerk darüber bleibt heil.',
    weak: 'Der Träger ist überlastet und bricht.',
    bouncy: 'Fest genug, aber er biegt sich stärker durch, als das Mauerwerk darüber verträgt. Es reißt.',
  },
  beams: {
    'timber-2x200': 'Holz 2 × 50×200',
    'glulam-270': 'Brettschichtholz 90×270',
    'glulam-360': 'Brettschichtholz 90×360',
    'ipe-140': 'Stahl IPE 140',
    'ipe-160': 'Stahl IPE 160',
    'ipe-200': 'Stahl IPE 200',
  },
  footings: {
    front: 'Vorne, Sand',
    back: 'Hinten, weicher Ton',
    frontWidth: 'Breite vorne',
    backWidth: 'Breite hinten',
    pressure: (q: string, allow: string) => `${q} von ${allow}`,
    ok: 'Beide Enden setzen sich ein wenig, und gleich. Das Haus bleibt gerade.',
    sinks: (where: string) => `${where}: Der Boden gibt nach und dieses Ende sinkt. Das Haus kippt.`,
  },
  bracing: {
    wall: 'Seitenwand, acht Felder',
    kinds: { plain: 'Einfach', brace: 'Ausgesteift', window: 'Fenster' },
    capacity: (q: string, demand: string) => `Widersteht ${q} von ${demand}`,
    count: (n: number, want: number) => `${n} von ${want} Fenstern`,
    each: (q: string) => `je ausgesteiftes Feld ${q}`,
    hint: 'Klicke ein Feld an, um es zu ändern: einfach, ausgesteift, Fenster.',
    ok: 'Die Wand steht, mit drei Fenstern.',
    racks: (cap: string, demand: string) => `Die ausgesteiften Felder widerstehen ${cap}, der Wind bringt ${demand}. Die Wand klappt um.`,
    ends: 'Insgesamt fest genug, aber eine nicht ausgesteifte Ecke lässt die Wand dort umklappen, wo die Kraft ankommt.',
    windows: (n: number) => `Sie steht, aber der Bauherr wollte ${n} Fenster.`,
  },
  iris: 'Nimm einen IPE 140. Das ist Stahl, also viel fester als jeder Holzträger. Über 3,6 m reicht das locker.',
};

/* ── Japanese ─────────────────────────────────────────────────────────────── */

export const ja: typeof en = {
  docTitle: '土木プレビュー · EngineerOS',
  kicker: '土木工学のプレビュー',
  title: '屋根から地面まで、一軒の家',
  lede:
    '一軒の家で5つの課題: 屋根、扉の上の梁、基礎、風に耐える壁、そしてIrisの確認。どれも土木技術者が下す判断で、' +
    'ページがそれを試験します。',
  honest: 'プレビューであり、モデルはわざと単純にしています。各ステップで省いていることを示します。実際の家は資格を持つ技術者が建築基準に従って設計します。',
  progress: (done: number, total: number) => `${total}つ中${done}つ完了`,
  finish: {
    title: '土木プレビューを修了しました',
    lede: '5つの判断を、すべて試験しました。土木の本コースを学びたいなら、下で教えてください。次に何を作るかはそれで決まります。',
  },

  steps: {
    rafters: {
      idea: '上からの荷重',
      title: '屋根の雪',
      story:
        '家は上から始まります。瓦と冬の雪がこの屋根の1平方メートルごとに1.5 kNでのしかかり、垂木がそれを壁まで運びます。' +
        '垂木を詰めれば荷重を分け合い、背を高くすればたわみが減ります。どちらも木材が要ります。',
      task: '垂木の間隔と背の高さを選んでください。木材の曲げの上限7.5 MPa以下に収め、屋根1メートルあたりの木材を20リットル以内にすること。',
      debrief:
        '正解は2つ: 背200 mmを600 mm間隔、または背150 mmを400 mm間隔。曲げの強さは背の2乗で増えるので、背を高くするのが' +
        '安上がりな強さです。実際の屋根もこうして、荷重から下へ順に計算されます。',
      leavesOut: '屋根を持ち上げる風、勾配、両端の接合部、そして設計基準が吹きだまりや長期荷重に加える係数。',
    },
    lintel: {
      idea: '梁と開口',
      title: 'ガレージの扉の上の梁',
      story:
        '施主は、上の床と屋根を支える壁に幅3 mのガレージの扉を付けたいと言います。荷重は1メートルあたり12 kN。' +
        '開口を切れば、何かがそこを渡らなければなりません。開口の上の梁は2通りに壊れます。折れるか、上のれんがが割れるほどたわむかです。',
      task: '十分に強く、十分に硬い梁を選んでください。応力は材料の上限以下、たわみはスパン÷360以下。',
      debrief:
        '鋼はずっと強いので、背160 mmの鋼の梁が集成材360 mmと同じ仕事をします。集成材270 mmは硬さは足りても折れていました。' +
        '強さと硬さは別の問いで、梁は両方に答えなければなりません。',
      leavesOut: '梁の両端がれんがに載る部分、梁の自重、耐火、そして支えのない鋼が横にねじれる性質。',
    },
    footings: {
      idea: '地盤',
      title: '同じ壁、違う地面',
      story:
        'すべては地面で終わります。各壁は1メートルあたり60 kNをコンクリートの布基礎に伝えます。地盤調査によれば、' +
        '前側は150 kPaに耐える締まった砂、後ろ側は75しか耐えない軟らかい粘土です。',
      task: '基礎ごとに幅を選び、その下の圧力が地盤の耐えられる範囲に収まるようにしてください。コンクリートはただではありません。',
      debrief:
        '軟らかい側には2倍以上の幅が要ります。間違えればその側だけが沈み、家は地面から割れていきます。だから地盤調査が最初なのです。',
      leavesOut: '年月をかけた沈下、地下水、凍結、そして季節で膨らみ縮む粘土。',
    },
    bracing: {
      idea: '風',
      title: '風に耐える壁にする',
      story:
        '妻側に当たる風が、両側の壁をそれぞれ19.2 kNで押します。ただのパネルの壁はトランプの家のように倒れますが、' +
        '筋かいや合板で固めたパネルは5 kNに耐えます。施主はこの壁に窓を3つ欲しがっています。',
      task: '8枚のパネルをクリックして、ただの壁、筋かい、窓に切り替えてください。風に耐え、両端の角を固め、窓を3つ残すこと。',
      debrief:
        '筋かいパネル4枚で20 kN、19.2をわずかに上回り、力の入る角も固まっています。窓はすべて固められないパネルです。' +
        'だから建築家と技術者は壁をめぐって交渉するのです。',
      leavesOut: 'ほかの向きからの風、力を壁へ伝える屋根と床、そして筋かいパネルの浮き上がりを止める金物。',
    },
    'civil-ai': {
      idea: 'AIを確かめる',
      title: 'Irisが鋼の梁を選ぶ',
      story: '今度は同じ壁に、もっと広い3.6 mの扉が欲しいそうです。Irisはもう答えを用意しています。',
      task: '3.6 mの開口でIrisの梁を試してください。だめなら、耐える梁を選びます。',
      debrief:
        'Irisは数字ではなく材料から考えました。曲げはスパンの2乗で増えるので、20%広ければ応力は44%増えます。' +
        '鋼が強いからといって、小さな鋼の梁が十分に強いとは限りません。',
      leavesOut: 'ガレージの梁が省いたものすべて。',
    },
  },

  rafters: {
    spacing: '間隔',
    depth: '背の高さ',
    stress: (q: string, allow: string) => `応力 ${q} / ${allow}`,
    timber: (q: string, budget: string) => `木材 ${q} / ${budget}`,
    ok: '雪を支え、木材も予算内です。',
    weak: (q: string) => `${q}で垂木が雪の重さに折れます。`,
    wasteful: (q: string) => `強さは足りますが、木材${q}は予算オーバーです。`,
  },
  lintel: {
    beam: '梁',
    opening: (q: string) => `開口 ${q}`,
    stress: (q: string, allow: string) => `応力 ${q} / ${allow}`,
    sag: (q: string, limit: string) => `たわみ ${q} / ${limit}`,
    ok: '強さも硬さも十分です。上のれんがは無事です。',
    weak: '梁の応力が上限を超え、折れます。',
    bouncy: '強さは足りますが、上のれんがが耐えられないほどたわみます。割れます。',
  },
  beams: {
    'timber-2x200': '木材 2 × 50×200',
    'glulam-270': '集成材 90×270',
    'glulam-360': '集成材 90×360',
    'ipe-140': '鋼 IPE 140',
    'ipe-160': '鋼 IPE 160',
    'ipe-200': '鋼 IPE 200',
  },
  footings: {
    front: '前側(砂)',
    back: '後ろ側(軟らかい粘土)',
    frontWidth: '前側の幅',
    backWidth: '後ろ側の幅',
    pressure: (q: string, allow: string) => `${q}(許容${allow})`,
    ok: '両側が少しずつ同じだけ沈みます。家は水平のままです。',
    sinks: (where: string) => `${where}: 地盤が負けてその側が沈みます。家が傾きます。`,
  },
  bracing: {
    wall: '側面の壁、パネル8枚',
    kinds: { plain: 'ただの壁', brace: '筋かい', window: '窓' },
    capacity: (q: string, demand: string) => `耐力 ${q} / ${demand}`,
    count: (n: number, want: number) => `窓 ${n} / ${want}`,
    each: (q: string) => `筋かいパネル1枚 ${q}`,
    hint: 'パネルをクリックすると、ただの壁、筋かい、窓の順に変わります。',
    ok: '壁は立っています。窓も3つあります。',
    racks: (cap: string, demand: string) => `筋かいパネルの耐力は${cap}、風は${demand}。壁が倒れます。`,
    ends: '合計の強さは足りますが、固めていない角から、力の入る所で壁が折れます。',
    windows: (n: number) => `立ってはいますが、施主は窓を${n}つ求めています。`,
  },
  iris: 'IPE 140にしましょう。鋼なので、どんな木の梁よりずっと強いです。3.6 mでも大丈夫です。',
};
